import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { SparkPoint } from "./shared";
import type { MonthsOnBookRow } from "./chargeoff-acceleration";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";

export type { SparkPoint };

export interface LossRecoverySummary {
  netLossRate: {
    current: string;
    targetPct: number;
    trend: { value: string; direction: "up" | "down" | "flat" };
    /** the first funded year the lifetime figure counts from, e.g. "2015" (null when the data does not say) */
    sinceYear?: string | null;
  };
  /** the net loss of this calendar year so far, e.g. "$5.8M" (an em dash where loss_recovery is not usable) */
  netLossYtd: { current: string };
  expectedLoss: { current: string };
  exposureAtDefault: { current: string };
  netLossTrend: Array<{ year: string; netLossRate: number }>;
  // Real latest file date, e.g. "Mar'26" -- replaces the old hardcoded
  // "(Aug'26)" in the Expected Loss tile's own label.
  asOfLabel: string;
  cards: {
    // null -- the real LGD table is still WIP. Render an empty state, not a
    // fabricated stat.
    lgd: { stat: string; sparkline: SparkPoint[] } | null;
    expectedLoss: { stat: string; sparkline: SparkPoint[] };
    lifetimeLossProjections: { stat: string; sparkline: SparkPoint[] };
    annualizedLoss: { stat: string; sparkline: SparkPoint[] };
    lossDevelopmentWal: { stat: string; sparkline: SparkPoint[] };
    // Always null here -- split into its own endpoint/hook
    // (useChargeoffCard below). Its only data source is a full-table scan
    // with no supporting index (~15-18s); fetched separately so this card
    // alone shows a loading state instead of the whole page waiting on it.
    chargeoffAcceleration: { stat: string; sparkline: SparkPoint[] } | null;
  };
}

export function useLossRecovery() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", ticket ?? "all"],
    queryFn: () => apiGet<LossRecoverySummary>("/loss-recovery", { ticket }),
  });
}

/** Net Loss and Recovery as % of Charge-offs, by Cohort: one row per funded year. `available` is false while the server has no
 *  loss_recovery table. The toggle picks the rows that are realized, projected, or of active cohorts. */
export function useNetLossRecoveryByCohort(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", "net-loss-recovery-by-cohort", basis, ticket ?? "all"],
    queryFn: () =>
      apiGet<{ available: boolean; rows: Array<{ year: string; netLoss: number; recovery: number }> }>(
        "/loss-recovery/net-loss-recovery-by-cohort",
        { ticket, basis },
      ),
  });
}

export function useChargeoffCard() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", "chargeoff-card", ticket ?? "all"],
    queryFn: () => apiGet<{ stat: string; sparkline: SparkPoint[] }>("/loss-recovery/chargeoff-card", { ticket }),
  });
}

/** Vintage Analysis of Net Loss: one column per funded year by months on book (0-72), in % of the year's financed amount. `available` is
 *  false while the server has no vintage_net_loss_charts table. The toggle picks the rows that are realized, projected, or of active cohorts. */
export function useNetLossVintage(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", "net-loss-vintage", basis, ticket ?? "all"],
    queryFn: () =>
      apiGet<{ available: boolean; curve: MonthsOnBookRow[] }>("/loss-recovery/net-loss-vintage", {
        ticket,
        basis,
      }),
  });
}

/** One calendar month of a funded year: its net loss, the funded year's total net loss and the share between them (in %). */
export interface NetLossDistributionPoint {
  year: string;
  month: number;
  netLoss: number;
  yearTotal: number;
  rate: number;
}

/** Net loss distribution: one point per funded year x calendar month, for the ticket size. `available` is false while the server has no
 *  net_loss_distribution table. The toggle picks the rows that are realized, projected, or of active cohorts. */
export function useNetLossDistribution(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", "net-loss-distribution", basis, ticket ?? "all"],
    queryFn: () =>
      apiGet<{ available: boolean; points: NetLossDistributionPoint[] }>("/loss-recovery/net-loss-distribution", {
        ticket,
        basis,
      }),
  });
}

/** One row of the Net Loss Rate by Vintage table. Amounts are raw dollars; `netLossRate` is a % (null without a financed amount). */
export interface NetLossVintageRow {
  vintage: string;
  financedAmount: number | null;
  grossLoss: number;
  recovery: number;
  netLoss: number;
  netLossRate: number | null;
}

/** The Net Loss Rate by Vintage table: one row per funded year, for the ticket size and the toggle's rows (realized, projected, active). */
export function useNetLossByVintage(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["loss-recovery", "net-loss-by-vintage", basis, ticket ?? "all"],
    queryFn: () =>
      apiGet<{ available: boolean; rows: NetLossVintageRow[] }>("/loss-recovery/net-loss-by-vintage", {
        ticket,
        basis,
      }),
  });
}
